// Линза-робот из частей (Алексей 25.10, #2337–#2341, #2349): в полёте и пока висит — векторный корпус
// без глаз и пламени (robotSvg.ts, перерисован по блокам с кадра 0 листа, #2779) + глаза, пламя и полосы
// скорости векторами (регулируются). Удар в стекло и «привет» — нарисованными кадрами листа
// (там робот естественно поворачивается лицом), ровным темпом и с короткой подменой кадров. Лист v4 — кадры 14…29 с выглаженным силуэтом и ровной обводкой (в исходных был рваный
// контур — те самые «артефакты по контуру» при ударе).
// Координаты частей — в системе кадра 320×320 листа, чтобы размеры и раскладка не поменялись.
import React from 'react';
import { ROBOT_BODY_SVG, ROBOT_EYES_SVG, ROBOT_FLAME_SVG } from './robotSvg';

export const ROBOT_BODY = '/brand/robot_body.webp';
export const ROBOT_SHEET = '/brand/robot_sheet_v4.webp';
const SHEET_FRAMES = 30;

export type RigEyes = 'open' | 'happy' | 'squeeze';
/** Кадры листа с перетеканием: a → b, mix 0…1. */
export type RigSprite = { a: number; b: number; mix: number };
export type RigPose = {
  /** Наклон корпуса, градусы (+ — вперёд по ходу, робот смотрит вправо). */
  tilt?: number;
  /** 0…1 — полосы скорости за спиной и длина пламени. */
  speed?: number;
  eyes?: RigEyes;
  /** 0…1 — насколько закрыты глаза (моргание). */
  blink?: number;
  /** Множитель длины пламени (с flameDown — тяга k из makeJet, длина ×k^0.7). */
  flame?: number;
  /** Вместо частей — нарисованные кадры листа (удар, привет). */
  sprite?: RigSprite;
};

// Пивот наклона — центр корпуса в кадре.
const PIVOT = '61% 53%';
const EYE_COLOR = '#6cecf7';
const EYES = [192, 229.5];
const EYE_CY = 165.5;
// Нарисованные кадры разного размера (лоб в экран — крупнее): ширина корпуса относительно кадра 0.
const FRAME_SCALE: Record<number, number> = { 16: 165 / 178, 17: 165 / 215, 18: 165 / 196 };
// Полосы скорости — как в нарисованном полёте: узкие клинья от спины корпуса назад, чуть неровные,
// тают к хвосту. Только на уровне корпуса — не под пламенем (#2349); с небольшим отрывом от корпуса,
// и немного выше середины тоже (#2352). [y, x начала (у спины, с зазором), длина, толщина, наклон]
const LINES: [number, number, number, number, number][] = [
  [130, 128, 34, 4.6, -0.6], [148, 113, 62, 6.2, 0.5], [168, 106, 96, 7.4, -0.9], [188, 110, 80, 6.8, 0.8], [206, 123, 58, 5.6, -0.5], [222, 138, 36, 4.4, 0.6],
];
const wedge = (x: number, y: number, len: number, h: number, slope: number) =>
  `M${x},${y - h / 2} C${x - len * 0.35},${y - h * 0.42} ${x - len * 0.7},${y - h * 0.18 + slope} ${x - len},${y + slope} C${x - len * 0.7},${y + h * 0.2 + slope} ${x - len * 0.35},${y + h * 0.46} ${x},${y + h / 2} Z`;

export function RobotParts() {
  const spriteStyle: React.CSSProperties = {
    backgroundImage: `url(${ROBOT_SHEET})`, backgroundSize: `${SHEET_FRAMES * 100}% 100%`, backgroundRepeat: 'no-repeat',
    opacity: 0, transformOrigin: PIVOT,
  };
  return (
    <>
      <div data-rig-parts className="absolute inset-0">
        {/* Полосы — вне наклона: всегда горизонтальные; под корпусом, чтобы шли из-за спины. */}
        <svg viewBox="0 0 320 320" className="absolute inset-0 size-full overflow-visible" aria-hidden>
          <defs>
            <linearGradient id="rig-line" x1="1" x2="0" y1="0" y2="0">
              <stop offset="0" stopColor="#32677c" stopOpacity="0.55" />
              <stop offset="0.55" stopColor="#32677c" stopOpacity="0.3" />
              <stop offset="1" stopColor="#32677c" stopOpacity="0" />
            </linearGradient>
          </defs>
          <g data-rig-lines fill="url(#rig-line)" opacity={0}>
            {LINES.map(([y, x, len, h, sl], i) => <path key={i} d={wedge(x, y, len, h, sl)} />)}
          </g>
        </svg>
        <div data-rig-tilt className="absolute inset-0" style={{ transformOrigin: PIVOT }}>
          <svg viewBox="0 0 320 320" className="absolute inset-0 size-full overflow-visible" aria-hidden>
            <g transform="translate(186.5 238)">
              {/* Пламя — форма оригинала (Алексей 26.10 #2667/#2671): вырезано из кадра 0 листа (всё, что
                  в кадре есть, а в корпусе нет), капля с хвостом влево, два тона — ровно оригинал, без растяжки и
                  на том же месте (#2674: растянутое вылезало правым краем за сопло). Верх продлён под сопло (корпус поверх), так
                  что при повороте и растяжке длины пламя от сопла не отрывается. */}
              <g data-rig-flame>
                <g transform="translate(-186.5 -238)" dangerouslySetInnerHTML={{ __html: ROBOT_FLAME_SVG }} />
              </g>
            </g>
          </svg>
          <svg viewBox="0 0 320 320" className="pointer-events-none absolute inset-0 size-full select-none overflow-visible" aria-hidden
            dangerouslySetInnerHTML={{ __html: ROBOT_BODY_SVG }} />
          <svg viewBox="0 0 320 320" className="absolute inset-0 size-full overflow-visible" aria-hidden>
            <g data-rig-eyes-open dangerouslySetInnerHTML={{ __html: ROBOT_EYES_SVG }} />
            <g data-rig-eyes-happy fill="none" stroke={EYE_COLOR} strokeWidth={5} strokeLinecap="round" opacity={0}>
              {EYES.map((cx) => <path key={cx} d={`M${cx - 8},${EYE_CY + 4} Q${cx},${EYE_CY - 11} ${cx + 8},${EYE_CY + 4}`} />)}
            </g>
          </svg>
        </div>
      </div>
      <div data-rig-spa className="absolute inset-0" style={spriteStyle} />
      <div data-rig-spb className="absolute inset-0" style={spriteStyle} />
    </>
  );
}

// «Тяжесть» в единицах кадра/с²: разгон дашбордного перелёта даёт наклон ~10–15°.
const JET_G = 1200;

/**
 * Физика реактивного ранца (Алексей 03.11 #8736). По положению робота на экране (px, y вниз) численно —
 * скорость и ускорение (конечные разности, сглаживание ~150 мс). Наклон θ = atan2(a_x, g + a_вверх) —
 * корпус клонится туда, куда разгоняется; на висении ≈ 0. Догоняет цель критически задемпфированной
 * пружиной (без перелёта), не больше ±20°. Тяга k = |(a_x, g + a_вверх)| / g: 1 — висит, больше — разгон
 * и толчок вверх (низ покачивания), меньше — оседает. unitPx — пикселей в единице кадра 320.
 * Отдаёт позу для makeRig: tilt — в системе робота (с учётом зеркала), flame — k.
 */
export function makeJet(unitPx: number) {
  const TAU = 150, OMEGA = 16, MAX_TILT = 20;
  let px = NaN, py = NaN, at = 0;
  let vx = 0, vy = 0, ax = 0, ay = 0, tilt = 0, tiltV = 0;
  return (x: number, y: number, now: number, mirror: boolean): { tilt: number; flame: number } => {
    const dtMs = now - at;
    if (!Number.isFinite(px) || dtMs <= 0 || dtMs > 250) {
      // Первый кадр или вкладка спала — без скачка скорости.
      px = x; py = y; at = now;
    } else {
      const dt = dtMs / 1000, a = 1 - Math.exp(-dtMs / TAU);
      const rvx = (x - px) / unitPx / dt, rvy = (y - py) / unitPx / dt;
      const nvx = vx + (rvx - vx) * a, nvy = vy + (rvy - vy) * a;
      ax += ((nvx - vx) / dt - ax) * a; ay += ((nvy - vy) / dt - ay) * a;
      vx = nvx; vy = nvy; px = x; py = y; at = now;
      const target = Math.max(-MAX_TILT, Math.min(MAX_TILT, (Math.atan2(ax, JET_G - ay) * 180) / Math.PI));
      // Критическое демпфирование, шагами не длиннее 8 мс.
      for (let left = dt; left > 0; left -= 0.008) {
        const h = Math.min(0.008, left);
        tiltV += (OMEGA * OMEGA * (target - tilt) - 2 * OMEGA * tiltV) * h;
        tilt += tiltV * h;
      }
      tilt = Math.max(-MAX_TILT, Math.min(MAX_TILT, tilt));
    }
    const k = Math.hypot(ax, JET_G - ay) / JET_G;
    const m = mirror ? -1 : 1;
    return { tilt: m * tilt, flame: k };
  };
}

/** Ставит позу частям робота внутри `bot` (без ререндера React). */
export function makeRig(bot: HTMLElement, opts: { flameDown?: boolean } = {}) {
  const q = <T extends Element>(s: string) => bot.querySelector(s) as T | null;
  const parts = q<HTMLElement>('[data-rig-parts]');
  const tilt = q<HTMLElement>('[data-rig-tilt]');
  const lines = q<SVGGElement>('[data-rig-lines]');
  const flame = q<SVGGElement>('[data-rig-flame]');
  const open = q<SVGGElement>('[data-rig-eyes-open]');
  const happy = q<SVGGElement>('[data-rig-eyes-happy]');
  const spa = q<HTMLElement>('[data-rig-spa]');
  const spb = q<HTMLElement>('[data-rig-spb]');
  const lineEls = lines ? Array.from(lines.querySelectorAll('path')) : [];
  const eyeEls = open ? Array.from(open.querySelectorAll('path')) : [];
  const frameAt = (el: HTMLElement | null, frame: number, opacity: number, tiltDeg: number) => {
    if (!el) return;
    el.style.opacity = opacity.toFixed(3);
    if (opacity <= 0) return;
    el.style.backgroundPosition = `${(frame / (SHEET_FRAMES - 1)) * 100}% 0`;
    el.style.transform = `rotate(${tiltDeg.toFixed(2)}deg) scale(${FRAME_SCALE[frame] ?? 1})`;
  };
  return (pose: RigPose, now: number) => {
    let p = pose;
    // Кадр 0 листа = робот из частей: вместо него показываем части (тот же корпус, свои глаза и
    // пламя) — на стыке с полётом/висением ничего не подменяется.
    if (p.sprite && p.sprite.a === 0 && (p.sprite.b === 0 || p.sprite.mix <= 0)) p = { ...p, sprite: undefined };
    if (p.sprite) {
      // Перетекание: новый кадр проявляется, старый уходит во второй половине — середина не просвечивает.
      if (parts) parts.style.opacity = '0';
      const { a, b, mix } = p.sprite;
      const m = Math.max(0, Math.min(1, mix));
      frameAt(spa, a, m < 0.5 ? 1 : 2 * (1 - m), p.tilt ?? 0);
      frameAt(spb, b, m < 0.5 ? 2 * m : 1, p.tilt ?? 0);
      return;
    }
    if (parts) parts.style.opacity = '1';
    frameAt(spa, 0, 0, 0); frameAt(spb, 0, 0, 0);
    const speed = Math.max(0, Math.min(1, p.speed ?? 0));
    if (tilt) tilt.style.transform = `rotate(${(p.tilt ?? 0).toFixed(2)}deg)`;
    if (lines) {
      lines.setAttribute('opacity', Math.min(1, speed * 1.5).toFixed(3));
      // Клинья «дышат» длиной, каждый в своей фазе — как штрихи в нарисованном полёте.
      lineEls.forEach((el, i) => {
        const [y, x, len, h, sl] = LINES[i];
        const l = len * (0.55 + 0.45 * speed) * (0.85 + 0.15 * Math.sin(now / 95 + i * 1.7));
        el.setAttribute('d', wedge(x, y, l, h, sl));
      });
    }
    if (flame) {
      // Мерцание еле заметное и медленное (#2483 «рябит», #2503 «дребезжит») — только длина и яркость.
      const flicker = 1 + 0.012 * Math.sin(now / 260);
      if (opts.flameDown) {
        // Алексей 03.11 #8747: пламя — капля оригинала как есть (форма, цвета, своё мерцание), без своего угла:
        // оно в слое корпуса и наклоняется вместе с ним (наклон — makeJet). Масштаб только по длине
        // от сопла — форма не меняется.
        const k = Math.max(0.5, p.flame ?? 1);
        // Длина во всех фазах — базовая длина оригинала (0.9, без «+35 % на месте», Алексей #8754), сверху ×k^0.7.
        const len = 0.9 * flicker * k ** 0.7;
        flame.setAttribute('transform', `scale(1 ${len.toFixed(3)})`);
      } else {
        const len = 0.9 * (p.flame ?? 1) * (1 + 0.05 * speed) * flicker;
        // Куда бьёт струя (#2389): на месте — вниз, и гасит раскачку: корпус качнулся вперёд —
        // струя уходит назад, толкая низ вперёд (и наоборот). В полёте вперёд — назад тем сильнее, чем
        // быстрее. Угол в мире минус наклон корпуса = поворот в слое корпуса; поворот вокруг сопла.
        const tiltDeg = p.tilt ?? 0;
        const world = speed * 25 + (1 - speed) * 1.2 * tiltDeg;
        flame.setAttribute('transform', `rotate(${(world - tiltDeg).toFixed(1)}) scale(1 ${len.toFixed(3)})`);
      }
    }
    const eyes = p.eyes ?? 'open';
    if (open) {
      open.setAttribute('opacity', eyes === 'open' ? '1' : '0');
      // Моргание сплющивает глаз и чуть расширяет — к закрытию полоска, а не точка (#2783).
      const b = Math.max(0, Math.min(1, p.blink ?? 0));
      const sy = Math.max(0.12, 1 - b), sx = 1 + 0.45 * b;
      eyeEls.forEach((el, i) => {
        const cx = EYES[i] ?? EYES[0];
        el.setAttribute('transform', `translate(${cx} ${EYE_CY}) scale(${sx.toFixed(3)} ${sy.toFixed(3)}) translate(${-cx} ${-EYE_CY})`);
      });
    }
    if (happy) happy.setAttribute('opacity', eyes === 'happy' ? '1' : '0');
  };
}

export type BotPose = RigPose & { k?: number; sx?: number; sy?: number; dx?: number; dy?: number };

const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
const clamp01 = (u: number) => Math.max(0, Math.min(1, u));
const inQuad = (u: number) => u * u;
const outCubic = (u: number) => 1 - (1 - u) ** 3;
const smooth = (u: number) => u * u * (3 - 2 * u);

/** Моргание: 0 → 1 → 0 за `ms`. */
export function blinkAt(now: number, blinkStart: number, ms = 170): number {
  const u = (now - blinkStart) / ms;
  return u < 0 || u > 1 ? 0 : 1 - Math.abs(2 * u - 1);
}

/** Последовательность кадров по ключевым точкам [мс, кадр]: между соседними — перетекание. */
function seq(t: number, keys: [number, number][]): RigSprite {
  if (t <= keys[0][0]) return { a: keys[0][1], b: keys[0][1], mix: 0 };
  for (let i = 1; i < keys.length; i++) {
    const [t1, f1] = keys[i];
    const [t0, f0] = keys[i - 1];
    // Смена кадра — мгновенной подменой в конце отрезка: при смешивании антенна оставляла «тлеющий
    // след» (#2353), а пламя двух кадров расходилось и «отрывалось» от корпуса (#2361).
    if (t < t1) return { a: f0, b: f1, mix: 0 };
  }
  const last = keys[keys.length - 1][1];
  return { a: last, b: last, mix: 0 };
}

// Удар в стекло (#2341, #2349, #2355, #2357): старыми кадрами — разворачивается лицом (0 → 16 → 17) и
// подлетает к стеклу (крупнее), бац — зажмурился (18), расплющило и отбросило; стоит на месте,
// приходя в себя (покачивается, ещё зажмурен), открывает глаза и только потом улетает назад —
// дугой в сторону и покачиваясь, а не по прямой. X/Y — сдвиг, «Z» — масштаб.
export const BONK_MS = 2900;
// Подлетает быстро (#2360), отлетает медленно; кадры: лицом (16 → 17), зажмурился (18), открыл глаза (17).
const BONK_KEYS: [number, number][] = [[0, 0], [80, 16], [170, 17], [250, 17], [262, 18], [1050, 18], [1160, 17], [2560, 17], [2720, 16], [2860, 0]];
const T_HIT = 260, T_SQ = 330, T_BACK = 500, T_GO = 1150;
export function bonkPose(t: number, height: number): BotPose | null {
  if (t >= BONK_MS) return null;
  // Наклон — только от горизонтальной скорости (#2358/#2361): несёт вперёд — клонится вперёд, назад —
  // назад; отдельного «виляния» поверх нет, поэтому поворот всегда в такт с перемещением.
  const p = bonkBase(t, height);
  const dxAt = (tt: number) => bonkBase(Math.max(0, Math.min(BONK_MS - 1, tt)), height).dx ?? 0;
  const vx = (dxAt(t + 16) - dxAt(t - 16)) / 32 * 1000 / height;
  const lean = Math.max(-12, Math.min(12, 20 * vx));
  return { ...p, sprite: seq(t, BONK_KEYS), tilt: lean + (p.tilt ?? 0) };
}
function bonkBase(t: number, height: number): BotPose {
  const GLASS = 1.28, HIT = 1.17;
  const h = height;
  if (t < T_HIT) {                                  // быстро подлетает к стеклу
    const u = inQuad(t / T_HIT);
    return { k: lerp(1, GLASS, u), dx: h * 0.03 * u, dy: h * 0.02 * u };
  }
  if (t < T_SQ) {                                   // бац: расплющило о стекло
    const u = (t - T_HIT) / (T_SQ - T_HIT);
    const sq = 0.15 * (1 - u * 0.4);
    return { k: GLASS, sx: 1 + sq, sy: 1 - sq, dx: h * 0.03, dy: h * 0.02 };
  }
  if (t < T_BACK) {                                 // отбросило — резко и недалеко, с подбросом
    const u = outCubic((t - T_SQ) / (T_BACK - T_SQ));
    const sq = 0.09 * (1 - u);
    return { k: lerp(GLASS, HIT, u), sx: 1 + sq, sy: 1 - sq, dx: lerp(h * 0.03, -h * 0.06, u), dy: lerp(h * 0.02, -h * 0.03, u) };
  }
  if (t < T_GO) {                                   // стоит на месте, приходит в себя: затухающее покачивание
    const u = (t - T_BACK) / (T_GO - T_BACK);
    const decay = Math.exp(-u * 2.4);
    return {
      k: HIT + 0.012 * Math.sin(u * Math.PI * 2) * decay,
      dx: -h * 0.06 + h * 0.03 * Math.sin(u * Math.PI * 3) * decay,
      dy: lerp(-h * 0.03, -h * 0.01, smooth(u)),
    };
  }
  // открыл глаза и медленно улетает назад одной плавной дугой (вбок и чуть вверх), без рывков
  const u = (t - T_GO) / (BONK_MS - T_GO);
  const e = smooth(u);
  return {
    k: lerp(HIT, 1, e),
    dx: -h * 0.06 * (1 - e) + h * 0.10 * Math.sin(Math.PI * e),
    dy: lerp(-h * 0.01, 0, e) - h * 0.04 * Math.sin(Math.PI * e),
  };
}

// Стук в экран у напоминания (#2133): подлетает лицом, три стука (17 ↔ 18), отходит.
export const KNOCKS = 3, KNOCK_IN = 380, KNOCK_MS = 270, KNOCK_OUT = 450;
export const KNOCK_TOTAL = KNOCK_IN + KNOCKS * KNOCK_MS + KNOCK_OUT;
export function knockPose(t: number, height: number): BotPose | null {
  if (t >= KNOCK_TOTAL) return null;
  const NEAR = 1.16, GLASS = 1.25;
  if (t < KNOCK_IN) {
    const u = outCubic(t / KNOCK_IN);
    return { sprite: seq(t, [[0, 0], [160, 16], [300, 17]]), k: lerp(1, NEAR, u), dy: height * 0.015 * u };
  }
  if (t < KNOCK_IN + KNOCKS * KNOCK_MS) {
    const u = ((t - KNOCK_IN) % KNOCK_MS) / KNOCK_MS;
    const dy = height * 0.015;
    if (u < 0.35) { const v = inQuad(u / 0.35); return { sprite: { a: 17, b: 18, mix: 0 }, k: lerp(NEAR, GLASS, v), dy }; }
    if (u < 0.55) { const v = (u - 0.35) / 0.2; const sq = 0.06 * (1 - v); return { sprite: { a: 17, b: 18, mix: v < 0.25 ? v * 4 : 1 }, k: GLASS, sx: 1 + sq, sy: 1 - sq, dy }; }
    const v = outCubic((u - 0.55) / 0.45);
    return { sprite: { a: 18, b: 17, mix: smooth(Math.min(1, v * 2)) }, k: lerp(GLASS, NEAR, v), dy };
  }
  const tt = t - KNOCK_IN - KNOCKS * KNOCK_MS;
  const u = outCubic(tt / KNOCK_OUT);
  return { sprite: seq(tt, [[0, 17], [200, 16], [380, 0]]), k: lerp(NEAR, 1, u), dy: lerp(height * 0.015, 0, u) };
}

// Привет (#2338/#2349): нарисованные кадры покачивания 22…29 — с перетеканием и ровным темпом
// (раньше 8 кадров с разной длительностью щёлкали). Начало и конец — кадр 0, стык с частями невиден.
export const WAVE_MS = 2000;
const WAVE_KEYS: [number, number][] = [[0, 0], [160, 22], [380, 23], [600, 24], [820, 25], [1060, 26], [1300, 27], [1520, 28], [1720, 29], [1880, 0]];
export function wavePose(t: number, height: number): BotPose | null {
  if (t >= WAVE_MS) return null;
  const env = Math.sin(Math.PI * clamp01(t / WAVE_MS));
  return { sprite: seq(t, WAVE_KEYS), dy: -height * 0.025 * env * Math.abs(Math.sin(2 * Math.PI * t / 700)) };
}

// Геометрия кадра для тех, кто ставит робота у себя (экран «сервер обновляется», #2635) — те же числа,
// что у дашборда смены: кадр 320×320, робот обычного размера — полоса 240 px (по 40 px сверху и снизу
// под «лоб в экран»); корпус в кадре x 113…277 (слева место под шлейф).
export const RIG_AR = 320 / 240;
export const RIG_FH = 320 / 240, RIG_PAD = 40 / 240;
export const RIG_BODY_L = 113 / 320, RIG_BODY_R = 277 / 320, RIG_BODY_C = 195 / 320;
